import { Link } from 'react-router-dom';
import { useLanguage } from '../core/hooks/use-language';
import { useTranslation } from '../core/hooks/use-translation';
import { company, telHref } from '../site/company';
import type { TranslationKey } from '../core/i18n/translation';

const SERVICES: readonly TranslationKey[] = [
    'site.footer.svc1',
    'site.footer.svc2',
    'site.footer.svc3',
    'site.footer.svc4',
    'site.footer.svc5'
];

/**
 * The site footer, ported from the live static site.
 *
 * **The year is interpolated rather than written into the translation.** The live site hard-codes
 * `2026` in both languages, which is a string that quietly goes stale and does so twice.
 */
export function SiteFooter() {
    const { t } = useTranslation();
    const language = useLanguage();

    return (
        <footer className="border-t border-slate-200 bg-slate-900 text-slate-300">
            <div className="mx-auto max-w-6xl px-4 py-12">
                <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <div className="text-lg font-bold text-white">{t('brand.name')}</div>
                        <p className="mt-3 text-sm">{t('site.footer.desc')}</p>
                        <a
                            href={telHref(company.phone)}
                            className="mt-4 inline-block font-semibold text-white hover:text-brand-300"
                        >
                            {company.phone}
                        </a>
                        <p className="mt-4 text-sm text-brand-300">{t('site.footer.tagline')}</p>
                    </div>

                    <div>
                        <h2 className="font-semibold text-white">{t('site.footer.nav')}</h2>
                        <ul className="mt-3 space-y-2 text-sm">
                            <li>
                                <Link to={`/${language}`} className="hover:text-white">
                                    {t('site.nav.home')}
                                </Link>
                            </li>
                            <li>
                                <Link to={`/${language}#about`} className="hover:text-white">
                                    {t('site.nav.about')}
                                </Link>
                            </li>
                            <li>
                                <Link to={`/${language}#mortgage-programs`} className="hover:text-white">
                                    {t('site.nav.programs')}
                                </Link>
                            </li>
                            <li>
                                <Link to={`/${language}#faq`} className="hover:text-white">
                                    {t('site.footer.faq')}
                                </Link>
                            </li>
                            <li>
                                <Link to={`/${language}#partners`} className="hover:text-white">
                                    {t('site.footer.partners')}
                                </Link>
                            </li>
                            <li>
                                <Link to={`/${language}/blog`} className="hover:text-white">
                                    {t('nav.blog')}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h2 className="font-semibold text-white">{t('site.footer.services')}</h2>
                        <ul className="mt-3 space-y-2 text-sm">
                            {SERVICES.map((key) => (
                                <li key={key}>{t(key)}</li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h2 className="font-semibold text-white">{t('site.footer.socials')}</h2>
                        <ul className="mt-3 space-y-2 text-sm">
                            <li>
                                <a href={company.whatsApp} target="_blank" rel="noreferrer noopener" className="hover:text-white">
                                    WhatsApp
                                </a>
                            </li>
                            <li>
                                <a href={company.instagram} target="_blank" rel="noreferrer noopener" className="hover:text-white">
                                    Instagram
                                </a>
                            </li>
                            <li>
                                <a href={company.youTube} target="_blank" rel="noreferrer noopener" className="hover:text-white">
                                    YouTube
                                </a>
                            </li>
                            <li>
                                <a href={company.tikTok} target="_blank" rel="noreferrer noopener" className="hover:text-white">
                                    TikTok
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-slate-700 pt-6 text-sm">
                    <span>{t('site.footer.copy').replace('{year}', String(new Date().getFullYear()))}</span>
                    <span className="flex gap-4">
                        <Link to={`/${language}/policy`} className="hover:text-white">
                            {t('site.footer.policy')}
                        </Link>
                        <Link to={`/${language}/policy`} className="hover:text-white">
                            {t('site.footer.terms')}
                        </Link>
                    </span>
                </div>
            </div>
        </footer>
    );
}
